import { PROPERTY_TYPES, PROPERTY_TYPE_LABELS, labelledOptions } from '@resida/shared';
import { ProfileSection } from './ProfileSection';
import { inputClass, labelClass } from './fieldClasses';
import type { MarketingDraft } from './marketingDraft';

const PROPERTY_TYPE_OPTIONS = labelledOptions(PROPERTY_TYPES, PROPERTY_TYPE_LABELS);

const FLOOR_COVERING_TYPES = [
  { value: 'carpet', label: 'Carpet' },
  { value: 'concrete', label: 'Concrete' },
  { value: 'hardwood', label: 'Hardwood' },
  { value: 'laminate', label: 'Laminate' },
  { value: 'linoleum_vinyl', label: 'Linoleum / Vinyl' },
  { value: 'slate', label: 'Slate' },
  { value: 'softwood', label: 'Softwood' },
  { value: 'tile', label: 'Tile' },
  { value: 'other', label: 'Other' },
];

interface Props {
  draft: MarketingDraft;
  onChange: (patch: Partial<MarketingDraft>) => void;
  urlError?: string | null;
}

export function ProfileSectionPropertyDetails({ draft, onChange, urlError }: Props) {
  return (
    <ProfileSection title="Property details" testId="marketing-details-property">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label htmlFor="property-type" className={labelClass}>Property type</label>
          <select
            id="property-type"
            value={draft.propertyType}
            onChange={(e) => onChange({ propertyType: e.target.value })}
            data-testid="profile-property-type-select"
            className={inputClass}
          >
            <option value="">— Select —</option>
            {PROPERTY_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="year-built" className={labelClass}>Year built</label>
          <input
            id="year-built"
            type="number"
            value={draft.yearBuilt}
            onChange={(e) => onChange({ yearBuilt: e.target.value })}
            min={1800}
            max={2100}
            placeholder="e.g. 1995"
            data-testid="profile-year-built-input"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="floor-level" className={labelClass}>Floor level</label>
          <input
            id="floor-level"
            type="number"
            value={draft.floorLevel}
            onChange={(e) => onChange({ floorLevel: e.target.value })}
            min={-10}
            max={200}
            placeholder="e.g. 3"
            data-testid="profile-floor-level-input"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="floor-covering" className={labelClass}>Floor covering</label>
          <select
            id="floor-covering"
            value={draft.floorCovering}
            onChange={(e) => onChange({ floorCovering: e.target.value })}
            data-testid="profile-floor-covering-select"
            className={inputClass}
          >
            <option value="">— Select —</option>
            {FLOOR_COVERING_TYPES.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
        <div className="sm:col-span-2 lg:col-span-4">
          <label htmlFor="virtual-tour-url" className={labelClass}>Virtual tour link</label>
          <input
            id="virtual-tour-url"
            type="url"
            value={draft.virtualTourUrl}
            onChange={(e) => onChange({ virtualTourUrl: e.target.value })}
            placeholder="https://my.matterport.com/..."
            data-testid="profile-virtual-tour-url-input"
            aria-invalid={urlError ? true : undefined}
            aria-describedby={urlError ? 'virtual-tour-url-error' : undefined}
            className={inputClass}
          />
          {urlError && (
            <p id="virtual-tour-url-error" role="alert" className="mt-1 text-xs text-destructive" data-testid="profile-virtual-tour-url-error">
              {urlError}
            </p>
          )}
        </div>
      </div>
    </ProfileSection>
  );
}
